import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface MobileContextType {
  isMobile: boolean;
  orientation: 'landscape' | 'portrait' | null;
}

interface Props {
  children: ReactNode;
}

const MOBILE_BREAKPOINT_QUERY = '(max-width: 1068px)';

const MobileContext = createContext<MobileContextType>({
  isMobile: false,
  orientation: null,
});

const isMobile = (): boolean => {
  const matchesBreakpoint = window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  const isMobileDeviceUA = /Mobi|Android|iPhone|iPod/i.test(
    navigator.userAgent
  );
  return matchesBreakpoint || isMobileDeviceUA;
};

const isIPad = () => {
  const isMac = navigator.userAgent.includes('Macintosh');
  const hasTouch =
    'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1;
  return navigator.userAgent.includes('iPad') || (isMac && hasTouch);
};

const getOrientation = () => {
  if (!isIPad()) return null;
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
};

export const MobileProvider: React.FC<Props> = ({ children }) => {
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [orientation, setOrientation] = useState<
    'landscape' | 'portrait' | null
  >(null);
  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);

    const updateIsMobile = () => setIsMobileDevice(isMobile());
    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
  }, []);

  useEffect(() => {
    setOrientation(getOrientation());
    window.addEventListener('resize', () => setOrientation(getOrientation()));
  }, []);

  return (
    <MobileContext.Provider
      value={{ isMobile: isMobileDevice, orientation: orientation }}
    >
      {children}
    </MobileContext.Provider>
  );
};

export const useMobile = (): MobileContextType => useContext(MobileContext);
